import { IDecayResult, IDecayEstimate } from '@models/decay/decay-estimate';
import { getDateString } from './dates';

export const processDecayBalanceResult = (decayResult: IDecayResult): IDecayResult => {
  // Filling missing values
  decayResult = fillMissingBalances(decayResult);

  // Formatting the dates
  formatDecayBalanceVintages(decayResult);

  // Find last continuous Average Balance (the first balance before the first null one)
  const index = decayResult.ObservationAverages.findIndex(
    (b, i) => decayResult.ObservationAverages[i + 1] && decayResult.ObservationAverages[i + 1].Value === null
  );
  decayResult.LastContinuousValue =
    index >= 0 ? decayResult.ObservationAverages[index] : decayResult.ObservationAverages[decayResult.ObservationAverages.length - 1];

  return decayResult;
};

export const formatDecayBalanceVintages = (balanceResult: IDecayResult): IDecayResult => {
  // Format Vintages dates
  for (let i = 0; i < balanceResult.VintageObservationValues.length; i++) {
    const vintage = balanceResult.VintageObservationValues[i].Vintage;
    balanceResult.VintageObservationValues[i].Vintage = getDateString(vintage);
  }
  balanceResult.vintages = [...new Set(balanceResult.VintageObservationValues.filter(obs => obs.Vintage).map(obs => obs.Vintage))].sort();

  return balanceResult;
};

/**
 * **<9262>** Filling missing observations in `VintageObservationBalances` and `ObservationAverages` with _NULL_ balances
 */
const fillMissingBalances = (balances: IDecayResult): IDecayResult => {
  if (balances.VintageObservationValues.length > 1) {
    const sortedBalances = balances.VintageObservationValues.sort((a, b) => a.Observation - b.Observation);
    const first = sortedBalances[0];
    const last = sortedBalances[sortedBalances.length - 1];

    const allObservations = new Array(last.Observation - first.Observation + 1).fill(null).map((e, i) => ++i);

    for (let i = 0; i < allObservations.length; i++) {
      const existingVintageObservation = balances.VintageObservationValues.find(vob => vob.Observation === allObservations[i]);
      if (!existingVintageObservation) {
        balances.VintageObservationValues.push({
          Vintage: null,
          Observation: allObservations[i],
          Value: null
        });
      }

      const existingAvg = balances.ObservationAverages.find(vob => vob.Observation === allObservations[i]);
      if (!existingAvg) {
        balances.ObservationAverages.push({
          Observation: allObservations[i],
          Value: null
        });
      }
    }
    balances.ActualObservations = balances.VintageObservationValues[balances.VintageObservationValues.length - 1].Observation;
    balances.ObservationAverages = balances.ObservationAverages.sort((a, b) => a.Observation - b.Observation);
    balances.VintageObservationValues = balances.VintageObservationValues.sort((a, b) => a.Observation - b.Observation);
  }

  return balances;
};

/**
 * Filters out any negative observational averages where the observation is < 0.
 * This happens when the user has inputted invalid data that results in negative
 *  observations (i.e. Account Open date on incoming data is after EOP DAte).
 */
export const filterNegativesFromResult = (balanceResult: IDecayResult): IDecayResult => {
  if (balanceResult.ObservationAverages) {
    balanceResult.ObservationAverages = balanceResult.ObservationAverages.filter(val => val.Observation > 0);
    balanceResult.ActualObservations = balanceResult.ObservationAverages.length;
  }

  if (balanceResult.VintageObservationValues) {
    balanceResult.VintageObservationValues = balanceResult.VintageObservationValues.filter(val => val.Observation > 0);
  }
  balanceResult.StartDate =
    balanceResult.VintageObservationValues && balanceResult.VintageObservationValues.length > 0
      ? balanceResult.VintageObservationValues[0].Vintage
      : null;
  return balanceResult;
};

const ALL_COLOURS = ['#4572A7', '#AA4643', '#89A54E', '#80699B', '#3D96AE', '#DB843D', '#92A8CD', '#A47D7C', '#B5CA92'];
const ALL_COLOURS_LIGHTER = ['#a8bfdb', '#e9cac9', '#d2deb9', '#c0b4cd', '#a0d1de', '#eab68b', '#cdd7e8', '#c2cee3', '#cbdab2'];

/**
 *
 */
export const setEstimateColours = (estimates: IDecayEstimate[], isExport = false) => {
  const chosenColours = isExport ? ALL_COLOURS_LIGHTER : ALL_COLOURS;

  if (estimates) {
    estimates.forEach((est, i) => {
      if (!est.Colour) {
        if (i >= chosenColours.length) {
          const colourIndex = i - Math.floor(i / chosenColours.length) * chosenColours.length;
          est.Colour = chosenColours[colourIndex];
        } else {
          est.Colour = chosenColours[i];
        }
      }
    });
  }
  return estimates;
};
